import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  ShoppingCart as PreOrderIcon,
  TrendingUp as TrendingIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  DateRange as DateIcon
} from '@mui/icons-material';
import { orderService, productService } from '../../services/firebase';
import { OrderStatus } from '../../types/models';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface PreOrderSummary {
  productName: string;
  category: string;
  totalQuantity: number;
  totalOrders: number;
  weightBreakdown: string;
  estimatedRevenue: number;
  isOrganic: boolean;
}

interface DateWisePreOrder {
  date: string;
  displayDate: string;
  totalOrders: number;
  totalProducts: number;
  totalRevenue: number;
  productSummaries: PreOrderSummary[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const PreOrders: React.FC = () => {
  const [preOrderSummaries, setPreOrderSummaries] = useState<PreOrderSummary[]>([]);
  const [dateWisePreOrders, setDateWisePreOrders] = useState<DateWisePreOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedDays, setSelectedDays] = useState(7); // Default to 7 days
  const [totalStats, setTotalStats] = useState({
    totalOrders: 0,
    totalProducts: 0,
    totalRevenue: 0
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [ordersData, productsData] = await Promise.all([
        orderService.getOrders(),
        productService.getProducts()
      ]);

      // Get date range for analysis
      const today = new Date();
      const dateWiseData = new Map<string, DateWisePreOrder>();
      
      // Initialize dates for the selected number of days starting from tomorrow
      for (let i = 1; i <= selectedDays; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        const displayDate = date.toLocaleDateString('en-IN', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        });
        
        dateWiseData.set(dateStr, {
          date: dateStr,
          displayDate,
          totalOrders: 0,
          totalProducts: 0,
          totalRevenue: 0,
          productSummaries: []
        });
      }

      // Filter pre-orders with status less than 'preparing' (pending, confirmed)
      const preOrders = ordersData.filter(order => {
        return order.status === OrderStatus.PENDING || order.status === OrderStatus.CONFIRMED;
      });

      // Process pre-orders data

      // Process orders by delivery date
      preOrders.forEach(order => {
        let deliveryDate = order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : null;
        
        // If no delivery date, assume it's for tomorrow (common for pre-orders)
        if (!deliveryDate) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          deliveryDate = tomorrow.toISOString().split('T')[0];
        }
        
        // Assign delivery date if not set
        
        if (deliveryDate && dateWiseData.has(deliveryDate)) {
          const dayData = dateWiseData.get(deliveryDate)!;
          dayData.totalOrders++;
          
          order.items.forEach(item => {
            // Handle embedded product data structure
            const itemAny = item as any;
            const productName = itemAny.name || item.product?.name || item.productName;
            if (!productName) return;

            // Use unitPrice if available, otherwise fallback to item.price
            const itemPrice = item.unitPrice || item.price || 0;
            dayData.totalRevenue += itemPrice * item.quantity;
          });
        }
      });

      // Now aggregate products for each date
      dateWiseData.forEach((dayData, dateStr) => {
        const productMap = new Map<string, PreOrderSummary>();
        
        // Get all orders for this specific date
        const dayOrders = preOrders.filter(order => {
          let orderDeliveryDate = order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : null;
          
          // If no delivery date, assume it's for tomorrow (common for pre-orders)
          if (!orderDeliveryDate) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            orderDeliveryDate = tomorrow.toISOString().split('T')[0];
          }
          
          return orderDeliveryDate === dateStr;
        });
        
        // Process orders for this date
        
        dayOrders.forEach(order => {
          order.items.forEach(item => {
            // Handle embedded product data structure
            const itemAny = item as any;
            const productId = itemAny.id || item.product?.id || item.productId;
            const productName = itemAny.name || item.product?.name || item.productName;
            const category = itemAny.category || item.product?.category || 'Uncategorized';
            const isOrganic = itemAny.isOrganic || item.product?.isOrganic || false;
            
            if (!productId || !productName) {
              return;
            }

            if (!productMap.has(productId)) {
              productMap.set(productId, {
                productName,
                category,
                totalQuantity: 0,
                totalOrders: 0,
                weightBreakdown: '',
                estimatedRevenue: 0,
                isOrganic
              });
            }

            const summary = productMap.get(productId)!;
            summary.totalQuantity += item.quantity;
            summary.totalOrders += 1;
            
            // Use unitPrice if available, otherwise fallback to item.price
            const itemPrice = item.unitPrice || item.price || 0;
            summary.estimatedRevenue += itemPrice * item.quantity;
            
            // Get raw unit from item
            const rawUnit = item.selectedWeight?.weight || item.unit || 'piece';
            const unitStr = rawUnit.toLowerCase().trim().replace(/\s+/g, '');
            
            // Convert to standard units and calculate totals
            const convertToStandardUnits = (unit: string, qty: number): { kg: number; litres: number; pieces: number } => {
              let kg = 0, litres = 0, pieces = 0;
              
              // Extract numeric value
              const numMatch = unit.match(/([\d.]+)/);
              const num = numMatch ? parseFloat(numMatch[1]) : 0;
              
              // Weight conversions
              if (unit.includes('kg')) {
                kg = num * qty;
              } else if (unit.endsWith('g') && !unit.includes('kg')) {
                kg = (num / 1000) * qty; // Convert grams to kg
              }
              // Volume conversions
              else if (unit.endsWith('l') && unit.length <= 3) {
                litres = num * qty;
              } else if (unit.includes('ml')) {
                litres = (num / 1000) * qty; // Convert ml to litres
              } else if (unit.includes('litre') || unit.includes('liter')) {
                litres = num * qty;
              }
              // Count-based units
              else if (unit === 'piece' || unit === 'pieces' || unit === 'pcs' || unit === 'pc') {
                pieces = qty;
              }
              // Unknown - assume pieces
              else {
                pieces = qty;
              }
              
              return { kg, litres, pieces };
            };
            
            // Parse existing totals
            let totalKg = 0, totalLitres = 0, totalPieces = 0;
            
            if (summary.weightBreakdown && summary.weightBreakdown !== '') {
              const kgMatch = summary.weightBreakdown.match(/([\d.]+)\s*kg/);
              const litresMatch = summary.weightBreakdown.match(/([\d.]+)\s*(?:litres?|l\b)/);
              const piecesMatch = summary.weightBreakdown.match(/([\d.]+)\s*pieces?/);
              
              if (kgMatch) totalKg = parseFloat(kgMatch[1]);
              if (litresMatch) totalLitres = parseFloat(litresMatch[1]);
              if (piecesMatch) totalPieces = parseFloat(piecesMatch[1]);
            }
            
            // Add current item's contribution
            const contribution = convertToStandardUnits(unitStr, item.quantity);
            totalKg += contribution.kg;
            totalLitres += contribution.litres;
            totalPieces += contribution.pieces;
            
            // Format aggregated totals for easy procurement
            const breakdownParts: string[] = [];
            
            if (totalKg > 0) {
              // Show with 2 decimal places if needed, otherwise as integer
              const kgDisplay = totalKg % 1 === 0 ? totalKg.toString() : totalKg.toFixed(2);
              breakdownParts.push(`${kgDisplay} kg`);
            }
            
            if (totalLitres > 0) {
              const litresDisplay = totalLitres % 1 === 0 ? totalLitres.toString() : totalLitres.toFixed(2);
              breakdownParts.push(`${litresDisplay} litres`);
            }
            
            if (totalPieces > 0) {
              breakdownParts.push(`${totalPieces} pieces`);
            }
            
            summary.weightBreakdown = breakdownParts.join(', ') || '0 kg'
          });
        });
        
        dayData.productSummaries = Array.from(productMap.values());
        dayData.totalProducts = productMap.size;
        
        // Store results for this date
      });

      // Calculate cumulative totals for tomorrow (first day)
      const tomorrowData = Array.from(dateWiseData.values())[0];
      if (tomorrowData) {
        setPreOrderSummaries(tomorrowData.productSummaries);
        setTotalStats({
          totalOrders: tomorrowData.totalOrders,
          totalProducts: tomorrowData.totalProducts,
          totalRevenue: tomorrowData.totalRevenue
        });
      }
      
      setDateWisePreOrders(Array.from(dateWiseData.values()));

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDaysChange = (event: SelectChangeEvent<number>) => {
    setSelectedDays(event.target.value as number);
  };

  const exportTodayRequirements = () => {
    // Export only tomorrow's (today's requirement) data
    const tomorrowData = dateWisePreOrders[0];
    if (!tomorrowData) {
      alert('No pre-orders available for tomorrow');
      return;
    }

    const doc = new jsPDF();
    
    // Header with emphasis on today's date
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Today\'s Procurement Requirements', 20, 20);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`For Delivery: ${tomorrowData.displayDate}`, 20, 30);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 20, 38);
    
    // Summary box
    doc.setFillColor(240, 240, 240);
    doc.rect(20, 45, 170, 25, 'F');
    doc.setFontSize(10);
    doc.text(`Total Orders: ${tomorrowData.totalOrders}`, 25, 52);
    doc.text(`Products Needed: ${tomorrowData.totalProducts}`, 25, 60);
    doc.text(`Expected Revenue: ₹${tomorrowData.totalRevenue.toLocaleString()}`, 25, 68);
    
    // Product table
    autoTable(doc, {
      startY: 78,
      head: [['Product', 'Category', 'Quantity', 'Orders', 'Requirement', 'Revenue']],
      body: tomorrowData.productSummaries.map(product => [
        product.productName,
        product.category,
        product.totalQuantity.toString(),
        product.totalOrders.toString(),
        product.weightBreakdown,
        `₹${product.estimatedRevenue.toLocaleString()}`
      ]),
      theme: 'grid',
      headStyles: { fillColor: [124, 179, 66] },
      styles: { fontSize: 9 }
    });
    
    // Save with date in filename
    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`Shetkari_Requirements_${dateStr}.pdf`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('Shetkari Pre-Orders Report', 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 20, 30);
    doc.text(`Report Period: Next ${selectedDays} days`, 20, 40);
    
    let yPosition = 60;
    
    // Date-wise summary
    dateWisePreOrders.forEach((dayData, index) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Day header
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`${dayData.displayDate} (${dayData.date})`, 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Orders: ${dayData.totalOrders} | Products: ${dayData.totalProducts} | Revenue: ₹${dayData.totalRevenue.toLocaleString()}`, 20, yPosition);
      yPosition += 15;
      
      if (dayData.productSummaries.length > 0) {
        // Product table for this day
        const tableData = dayData.productSummaries.map(product => [
          product.productName,
          product.category,
          product.totalQuantity.toString(),
          product.totalOrders.toString(),
          product.weightBreakdown,
          `₹${product.estimatedRevenue.toLocaleString()}`,
          product.isOrganic ? 'Yes' : 'No'
        ]);
        
        autoTable(doc, {
          startY: yPosition,
          head: [['Product Name', 'Category', 'Total Quantity', 'Orders', 'Total Weight', 'Revenue', 'Organic']],
          body: tableData,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [76, 175, 80] },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable.finalY + 20;
      } else {
        doc.text('No pre-orders for this date', 25, yPosition);
        yPosition += 20;
      }
    });
    
    // Summary page
    doc.addPage();
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Overall Summary', 20, 30);
    
    const totalOrders = dateWisePreOrders.reduce((sum, day) => sum + day.totalOrders, 0);
    const totalRevenue = dateWisePreOrders.reduce((sum, day) => sum + day.totalRevenue, 0);
    const totalUniqueProducts = new Set(
      dateWisePreOrders.flatMap(day => day.productSummaries.map(p => p.productName))
    ).size;
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Total Orders: ${totalOrders}`, 20, 50);
    doc.text(`Total Revenue: ₹${totalRevenue.toLocaleString()}`, 20, 60);
    doc.text(`Unique Products: ${totalUniqueProducts}`, 20, 70);
    doc.text(`Average Daily Orders: ${(totalOrders / selectedDays).toFixed(1)}`, 20, 80);
    doc.text(`Average Daily Revenue: ₹${(totalRevenue / selectedDays).toLocaleString()}`, 20, 90);
    
    // Product Summary Table
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Product Summary - Total Quantities Needed', 20, 110);
    
    // Aggregate products across all dates
    const productTotals = new Map<string, {
      productName: string;
      category: string;
      totalQuantity: number;
      totalOrders: number;
      totalRevenue: number;
      isOrganic: boolean;
    }>();
    
    dateWisePreOrders.forEach(dayData => {
      dayData.productSummaries.forEach(product => {
        if (!productTotals.has(product.productName)) {
          productTotals.set(product.productName, {
            productName: product.productName,
            category: product.category,
            totalQuantity: 0,
            totalOrders: 0,
            totalRevenue: 0,
            isOrganic: product.isOrganic
          });
        }
        
        const total = productTotals.get(product.productName)!;
        total.totalQuantity += product.totalQuantity;
        total.totalOrders += product.totalOrders;
        total.totalRevenue += product.estimatedRevenue;
      });
    });
    
    const productSummaryData = Array.from(productTotals.values())
      .sort((a, b) => b.totalQuantity - a.totalQuantity)
      .map(product => [
        product.productName,
        product.category,
        product.totalQuantity.toString(),
        product.totalOrders.toString(),
        `₹${product.totalRevenue.toLocaleString()}`,
        product.isOrganic ? 'Yes' : 'No'
      ]);
    
    autoTable(doc, {
      startY: 120,
      head: [['Product Name', 'Category', 'Total Quantity', 'Total Orders', 'Total Revenue', 'Organic']],
      body: productSummaryData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [33, 150, 243] },
      margin: { left: 20, right: 20 }
    });
    
    // Save the PDF
    doc.save(`shetkari-preorders-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const formatCurrency = (amount: number) => `₹${amount.toLocaleString()}`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Pre-Orders Planning Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
              Cumulative date-wise pre-orders for inventory planning
            </Typography>
          </Box>
          <IconButton onClick={loadData} disabled={loading} sx={{ display: { xs: 'block', md: 'none' } }}>
            <RefreshIcon />
          </IconButton>
        </Box>
        
        {/* Action Buttons - Mobile Responsive */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            <InputLabel>Days</InputLabel>
            <Select
              value={selectedDays}
              label="Days"
              onChange={handleDaysChange}
            >
              <MenuItem value={3}>3 Days</MenuItem>
              <MenuItem value={7}>7 Days</MenuItem>
              <MenuItem value={14}>14 Days</MenuItem>
              <MenuItem value={30}>30 Days</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={exportTodayRequirements}
            disabled={loading || dateWisePreOrders.length === 0}
            sx={{ 
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: { xs: '100%', sm: 'auto' },
              fontWeight: 600
            }}
          >
            Export Today's Requirements
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={exportToPDF}
            disabled={loading || dateWisePreOrders.length === 0}
            sx={{ 
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: { xs: '100%', sm: 'auto' },
              display: { xs: 'flex', sm: 'inline-flex' }
            }}
          >
            Full Report
          </Button>
          
          <IconButton onClick={loadData} disabled={loading} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Tomorrow's Summary" icon={<PreOrderIcon />} />
          <Tab label="Date-wise Planning" icon={<DateIcon />} />
        </Tabs>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tomorrow's Summary Tab */}
      <TabPanel value={tabValue} index={0}>
        {!loading && (
          <>
            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      <PreOrderIcon />
                    </Avatar>
                    <Typography variant="h6">Total Orders</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totalStats.totalOrders}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                      <InventoryIcon />
                    </Avatar>
                    <Typography variant="h6">Products Needed</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totalStats.totalProducts}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                      <TrendingIcon />
                    </Avatar>
                    <Typography variant="h6">Expected Revenue</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatCurrency(totalStats.totalRevenue)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Pre-Orders Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Total Quantity</TableCell>
                    <TableCell>Orders</TableCell>
                    <TableCell>Total Weight</TableCell>
                    <TableCell>Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preOrderSummaries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No pre-orders for tomorrow yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    preOrderSummaries.map((summary, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {summary.productName}
                            </Typography>
                            {summary.isOrganic && (
                              <Chip label="Organic" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={summary.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {summary.totalQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell>{summary.totalOrders}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {summary.weightBreakdown}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(summary.estimatedRevenue)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </>
        )}
      </TabPanel>

      {/* Date-wise Planning Tab */}
      <TabPanel value={tabValue} index={1}>
        {!loading && (
          <Box>
            {dateWisePreOrders.map((dayData, index) => (
              <Card key={dayData.date} sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {dayData.displayDate} ({dayData.date})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Chip 
                        label={`${dayData.totalOrders} Orders`} 
                        color="primary" 
                        size="small" 
                      />
                      <Chip 
                        label={`${dayData.totalProducts} Products`} 
                        color="secondary" 
                        size="small" 
                      />
                      <Chip 
                        label={formatCurrency(dayData.totalRevenue)} 
                        color="success" 
                        size="small" 
                      />
                    </Box>
                  </Box>
                  
                  {dayData.productSummaries.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Orders</TableCell>
                            <TableCell>Total Weight</TableCell>
                            <TableCell>Revenue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dayData.productSummaries.map((product, productIndex) => (
                            <TableRow key={productIndex}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {product.productName}
                                  </Typography>
                                  {product.isOrganic && (
                                    <Chip label="Organic" size="small" color="success" sx={{ ml: 1 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={product.category} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {product.totalQuantity}
                                </Typography>
                              </TableCell>
                              <TableCell>{product.totalOrders}</TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {product.weightBreakdown}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatCurrency(product.estimatedRevenue)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No pre-orders for this date
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default PreOrders;
